import { HANDSHAKE_REQUIRED_STATUS } from '@breetometer/utils/handshake';
import {
	type BaseQueryApi,
	createApi,
	type FetchArgs,
	fetchBaseQuery,
	type FetchBaseQueryError,
	type FetchBaseQueryMeta,
	type QueryReturnValue,
} from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import {
	ENV_TARGET_KEY,
	getLocationFrom,
	HIDE_TOAST_KEY,
	type IApiResponse,
	isSandboxEnvironment,
	LIVE_ENVIRONMENT,
	SANDBOX_ENVIRONMENT,
	showToast,
	type TargetEnvTypes,
} from 'BreetHelpers';

const prepareHeaders = (headers: Headers, { getState }: Pick<BaseQueryApi, 'getState'>) => {
	const pinnedEnv = headers.get(ENV_TARGET_KEY);
	if (pinnedEnv) {
		headers.set('x-breet-env', pinnedEnv);
		headers.delete(ENV_TARGET_KEY);
		return headers;
	}

	const targetEnvironment = (getState() as { app?: { targetEnvironment?: TargetEnvTypes } }).app?.targetEnvironment;
	headers.set('x-breet-env', isSandboxEnvironment(targetEnvironment) ? SANDBOX_ENVIRONMENT : LIVE_ENVIRONMENT);
	return headers;
};

const baseQuery = fetchBaseQuery({
	baseUrl: `${typeof window !== 'undefined' ? window.location.origin : ''}/proxy`,
	prepareHeaders,
	credentials: 'include',
});

const secureBaseQuery = fetchBaseQuery({
	baseUrl: `${typeof window !== 'undefined' ? window.location.origin : ''}/api/secure`,
	prepareHeaders,
	credentials: 'include',
});

const handleToastNotification = (
	result: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>,
	isGetRequest: boolean
) => {
	const successResp = result.data as IApiResponse | undefined;
	const errorResp = result.error?.data as IApiResponse | undefined;
	const isToastHidden = !!result.meta?.request.headers.get(HIDE_TOAST_KEY);

	if (!isToastHidden && !isGetRequest) {
		showToast(successResp?.message ? 'success' : 'error', successResp?.message ?? errorResp?.message ?? 'Something went wrong');
	}
};

const handleServerError = (result: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>) => {
	const errorReqStatusCode = typeof result.error?.status === 'number' && result.error.status;

	if (errorReqStatusCode && errorReqStatusCode >= 500) {
		showToast('error', 'Internal server error. Please be patient 🙏');
		return true;
	}
	return false;
};

const mutex = new Mutex();

const baseQueryWithReauth = async (args: string | FetchArgs, api: BaseQueryApi, extraOptions: object) => {
	await mutex.waitForUnlock();

	let result = await baseQuery(args, api, extraOptions);

	if (result.error?.status === HANDSHAKE_REQUIRED_STATUS) {
		result = await secureBaseQuery(args, api, extraOptions);
	}

	if (handleServerError(result)) {
		return result;
	}

	handleToastNotification(result, result.meta?.request.method === 'GET');

	result = await attemptTokenRefresh(api, extraOptions, args, result);

	if (!result.error && result.data && result.meta?.response?.status && !(result.data instanceof Blob)) {
		result = {
			data: {
				...(result.data as object),
				statusCode: result.meta.response.status,
			},
			meta: result.meta,
		};
	}

	return result;
};

const refreshAndRetry = async (api: BaseQueryApi, extraOptions: object, args: string | FetchArgs) => {
	if (mutex.isLocked()) {
		await mutex.waitForUnlock();
		return baseQuery(args, api, extraOptions);
	}

	const release = await mutex.acquire();
	try {
		const refreshResult = await baseQuery('/auth/refresh', api, extraOptions);
		if (!refreshResult.data) throw new Error();

		return await baseQuery(args, api, extraOptions);
	} catch (_e: unknown) {
		// logUserOut's listener closes modals and resets the API cache for every logout path.
		api.dispatch({ payload: undefined, type: 'app/logUserOut' });

		const locationFrom = getLocationFrom();
		const { default: router } = await import('../../../router');
		router.navigate('/getlost', { state: { locationFrom }, replace: true }).catch(console.error);
		console.error(_e);

		throw _e;
	} finally {
		release();
	}
};

const attemptTokenRefresh = async (
	api: BaseQueryApi,
	extraOptions: object,
	args: string | FetchArgs,
	resultArg: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>
) => {
	if (resultArg.error?.status !== 401) return resultArg;

	const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
	if (currentPath === '/login' || currentPath === '/getlost') return resultArg;

	const state = api.getState() as { app?: { isUserLoggedIn?: boolean } };
	if (state.app && !state.app.isUserLoggedIn) return resultArg;

	if (resultArg.meta?.response?.url.includes('kyb')) {
		api.dispatch({ payload: undefined, type: 'kybAuth/logKybUserOut' });
	}

	try {
		return await refreshAndRetry(api, extraOptions, args);
	} catch {
		return resultArg;
	}
};

export const apiSlice = createApi({
	reducerPath: 'api',
	baseQuery: baseQueryWithReauth,
	endpoints: () => ({}),
	tagTypes: [
		'Kyb',
		'User',
		'GetBanks',
		'GetBeneficiaries',
		'GetUserWallets',
		'GetUserPreference',
		'GenerateOTP',
		'GetSellAssets',
		'GetNotifications',
		'AllTransactions',
		'UserRewardPoints',
		'GetSavedWalletAddresses',
		'GetDeveloperIntegration',
		'GetTradesSubAccountWalletAddresses',
		'GetWebhookLogs',
		'GetWebhookLogById',
		'AllTeamMembers',
		'GetKybDetails',
	],
});
